import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { Button, Divider, Headline, Small } from "@fronterahealth/frontera-ui-components";

import useGeneratePDFFile from "@components/PDFGenerator/useGeneratePDFFile";
import { AddditionalDetails } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/AdditionalDetails/AdditionalDetails";
import { Behaviors } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/Behaviors/Behaviors";
import { EmergencyContact } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/ClientDetails/EmergencyContact";
import { GeneralDetails } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/ClientDetails/GeneralDetails";
import { Communication } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/Communication/Communication";
import { DevelopmentalMilestone } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/Development/DevelopmentalMilestone/DevelopmentalMilestone";
import { EarlyInterventionServices } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/Development/EarlyInterventionServices/EarlyInterventionServices";
import { Education } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/Development/Education/Education";
import { BirthHistory } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/Health/BirthHistory/BirthHistory";
import { MedicalHistory } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/Health/MedicalHistory/MedicalHistory";
import { GuardianDetails } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/HomeEnvironment/GuardianDetails";
import { LivingConditions } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/HomeEnvironment/LivingConditions";
import { Insurance } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/Insurance/Insurance";
import { Tendencies } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/Tendencies/Tendencies";
import { AllRoutes } from "@pages/ClientDetailsSubPages/IntakeForm/Links/config";
import { getCurrentPageTitle } from "@pages/ClientDetailsSubPages/IntakeForm/Links/utils";

export const IntakeFormPageSwitcher: React.FC = () => {
  const { pathname } = useLocation();
  const { generatePDFFile, loading } = useGeneratePDFFile();

  return (
    <div>
      <Headline size="medium" className="mb-4">
        {pathname.includes("all") ? "All Fields" : getCurrentPageTitle(pathname.split("intake_form/").pop() || "")}
        {pathname.includes("all") ? (
          <Small displayType="normal" className="my-2 flex flex-row justify-between align-middle">
            <i>
              You can use <code>ctrl/cmd + f</code> to search for any values across the clients intake form here
            </i>
            <Button
              text={loading ? "Loading...." : "Download PDF"}
              appearance="soft"
              onClick={async () => {
                const element = document.getElementById("allIntakeForms");

                const file = await generatePDFFile(element, ["btnRow", "add", "remove"]);
                if (file) {
                  const url = URL.createObjectURL(file);
                  window.open(url);
                }
              }}
            />
          </Small>
        ) : null}
      </Headline>
      <Content />
    </div>
  );
};

const Content = () => {
  const { pathname } = useLocation();

  const intakeFormPath = pathname.split("intake_form/").pop();

  switch (intakeFormPath as AllRoutes) {
    case "all":
      return (
        <div id="allIntakeForms">
          <div className="pageBreak">
            <Headline size="medium">General Details</Headline>
            <GeneralDetails />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Emergency Contact</Headline>
            <EmergencyContact />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Guardian Details</Headline>
            <GuardianDetails />
            <Divider />
          </div>

          <div className="pageBreak">
            <Headline size="medium">Living Conditions</Headline>
            <LivingConditions />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Insurance</Headline>
            <Insurance />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Medical History</Headline>
            <MedicalHistory />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Birth History</Headline>
            <BirthHistory />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Education</Headline>
            <Education />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Early Intervention Services</Headline>
            <EarlyInterventionServices />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Developmental Milestones</Headline>
            <DevelopmentalMilestone />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Behaviors</Headline>
            <Behaviors />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Communication</Headline>
            <Communication />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Tendencies</Headline>
            <Tendencies />
            <Divider />
          </div>
          <div className="pageBreak">
            <Headline size="medium">Additional Details</Headline>
            <AddditionalDetails />
            <Divider />
          </div>
        </div>
      );
    case "client-details":
      return <Navigate to="general-details" />;
    case "client-details/general-details":
      return <GeneralDetails />;
    case "client-details/emergency-contact":
      return <EmergencyContact />;
    case "home-environment/guardian-details":
      return <GuardianDetails />;
    case "home-environment/living-conditions":
      return <LivingConditions />;
    case "insurance":
      return <Insurance />;
    case "health/medical-history":
      return <MedicalHistory />;
    case "health/birth-history":
      return <BirthHistory />;
    case "development/education":
      return <Education />;
    case "development/early-intervention-services":
      return <EarlyInterventionServices />;
    case "development/developmental-milestones":
      return <DevelopmentalMilestone />;

    case "behaviors/diet":
    case "behaviors/general":
    case "behaviors/interests":
    case "behaviors/movement-&-play":
    case "behaviors/sensory":
    case "behaviors/sleep":
      return <Behaviors />;
    case "communication/non-verbal-communication":
    case "communication/verbal-communication":
      return <Communication />;
    case "tendencies/aggression":
    case "tendencies/attachment":
    case "tendencies/attention":
    case "tendencies/defiance":
    case "tendencies/executive-functions":
    case "tendencies/language":
    case "tendencies/mood":
    case "tendencies/odd-ideas":
    case "tendencies/social-skills":
    case "tendencies/somatic":
    case "tendencies/strengths":
    case "tendencies/trauma":
      return <Tendencies />;
    case "additional-details":
      return <AddditionalDetails />;
    default:
      return null;
  }
};
